/**
 * Custom error classes for API and application errors
 */

export class ApiError extends Error {
  constructor(message, status = null, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Network error occurred') {
    super(message);
    this.name = 'NetworkError';
  }
}

export class ValidationError extends Error {
  constructor(message, field = null) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
  }
}

/**
 * Error handler utility functions
 */
export const ErrorHandler = {
  /**
   * Handle API response errors consistently
   */
  async handleApiResponse(response) {
    if (!response.ok) {
      let errorMessage = 'Request failed';
      let errorData = null;

      try {
        errorData = await response.clone().json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }

      throw new ApiError(errorMessage, response.status, errorData);
    }

    return response;
  },

  /**
   * Handle network errors consistently
   */
  handleNetworkError(error) {
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new NetworkError('Unable to connect to the server. Please check your internet connection.');
    }
    throw error;
  },

  /**
   * Log errors securely (without exposing sensitive information)
   */
  logError(error, context = {}) {
    const errorInfo = {
      message: error.message,
      name: error.name,
      timestamp: new Date().toISOString(),
      context
    };

    // In development, log to console for debugging
    if (process.env.NODE_ENV === 'development') {
      console.error('Error:', error);
    }

    // In production, send to logging service
    if (process.env.NODE_ENV === 'production') {
      try {
        fetch('/api/index.php/log_error', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(errorInfo),
        }).catch(() => {
          // Silently fail if logging fails
        });
      } catch {
        // Silently fail if logging fails
      }
    }
  }
};